import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { Customer } from '../components/CustomerManagement';
import { VnDatenPanel } from '../components/VnDatenPanel';
import { getVnFormData, getVorgang, type VnFormData } from '../lib/vorgangApi';
import { api } from '../lib/api';
import { buildDiagnosticsText, fillFields } from '../lib/bafaFormFill';
import { buildVnPage1Felder, buildVnPage3EsfFelder, type VnConsultant } from '../lib/vnFormFelder';
import { ESF_GRUNDSAETZE_MARKER_ID } from '../lib/esfFragebogen';

interface ProfileResponse extends VnConsultant {
  advisorNumber?: string;
  teamOwnerAdvisorNumber?: string | null;
  teamOwnerContactPerson?: string | null;
}

export default function VnFormPage() {
  const [vnOttUrl, setVnOttUrl] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [consultant, setConsultant] = useState<VnConsultant | null>(null);
  const [formData, setFormData] = useState<VnFormData | null>(null);
  const [formLoading, setFormLoading] = useState(true);
  const [debugText, setDebugText] = useState<string | null>(null);
  // Welche BAFA-Unterseite der iframe gerade zeigt — steuert, welche "Formular ausfüllen"-
  // Aktion angeboten wird und welchen Hinweis das Panel rechts zeigt. Seite 2 (Monitoring-/
  // Zufriedenheitsfragen) und Seite 3 (ESF-Grundsätze) teilen sich dieselbe URL
  // (/ubf3vn/attachments) — unterscheidbar nur über ein bekanntes Feld im DOM, nicht über den
  // Pfad. "Seite 1" optimistisch als Start, der OTT-Link öffnet immer dort; onLoad korrigiert
  // bei weiterer Navigation.
  const [bafaPage, setBafaPage] = useState<'seite1' | 'esf-monitoring' | 'esf-grundsaetze' | 'andere'>('seite1');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleIframeLoad = () => {
    try {
      const pathname = iframeRef.current?.contentWindow?.location?.pathname || '';
      const doc = iframeRef.current?.contentDocument || null;
      if (!pathname.endsWith('/ubf3vn/attachments')) {
        setBafaPage(pathname.endsWith('/ubf3vn') ? 'seite1' : 'andere');
        return;
      }
      if (doc?.getElementById(ESF_GRUNDSAETZE_MARKER_ID)) setBafaPage('esf-grundsaetze');
      else if (doc?.getElementById('beratungsgrund.umsatzrueckgang1')) setBafaPage('esf-monitoring');
      else setBafaPage('andere');
    } catch {
      // Cross-Origin (z.B. während des BAFA-Logins, noch nicht im Proxy) — Zustand unverändert.
    }
  };

  // Prüfansicht: /vn-formular?vorgangId=21 zeigt das Panel ohne BAFA-Einmal-Link — zum Ansehen
  // und Testen der Daten, Vorschau- und Download-Knöpfe. Das BAFA-Formular bleibt dabei leer.
  const pruefVorgangId = new URLSearchParams(window.location.search).get('vorgangId');

  useEffect(() => {
    if (pruefVorgangId) {
      getVorgang(Number(pruefVorgangId))
        .then((v) => api.get<Customer>(`/api/customers/${v.customerId}`))
        .then(setCustomer)
        .catch(() => {});
      getVnFormData(Number(pruefVorgangId))
        .then(setFormData)
        .catch(() => {})
        .finally(() => setFormLoading(false));
    } else {
      const url = localStorage.getItem('vnOttUrl');
      const customerId = localStorage.getItem('vnCustomerId');
      const vorgangId = localStorage.getItem('vnVorgangId');
      // Sofort entfernen: der OTT-Link ist einmalig + sensibel, soll nicht persistieren.
      localStorage.removeItem('vnOttUrl');
      localStorage.removeItem('vnCustomerId');
      localStorage.removeItem('vnVorgangId');
      setVnOttUrl(url);

      if (customerId) {
        api.get<Customer>(`/api/customers/${customerId}`).then(setCustomer).catch(() => {});
      }

      if (vorgangId) {
        getVnFormData(Number(vorgangId))
          .then(setFormData)
          .catch(() => {})
          .finally(() => setFormLoading(false));
      } else {
        setFormLoading(false);
      }
    }

    // Berater-Profil — gleiches Muster wie AntragPage.tsx: eigene BAFA-ID schlägt die
    // des Team-Inhabers, sonst tritt der Team-Inhaber im Formular auf.
    api.get<ProfileResponse>('/api/auth/me').then((profile) => {
      setConsultant({
        ...profile,
        advisorNumber: profile.advisorNumber || profile.teamOwnerAdvisorNumber || undefined,
        contactPerson: profile.advisorNumber
          ? profile.contactPerson
          : (profile.teamOwnerContactPerson || profile.contactPerson),
      });
    }).catch(() => {});
  }, []);

  /** Diagnose-Hilfe: zeigt, welche Formularfelder im iframe tatsächlich ankommen — zum
   *  Abgleich der Label-Texte gegen das echte BAFA-Verwendungsnachweis-Formular. */
  const runDiagnostics = () => {
    let doc: Document | null = null;
    try {
      doc = iframeRef.current?.contentDocument || null;
    } catch (e) {
      setDebugText(`Kein Zugriff auf iframe-Inhalt (Cross-Origin): ${String(e)}`);
      return;
    }
    if (!doc) {
      setDebugText('Kein Zugriff auf iframe-Inhalt (contentDocument ist null — vermutlich andere Domain / Cross-Origin, z.B. während des Logins).');
      return;
    }
    setDebugText(buildDiagnosticsText(doc, iframeRef.current?.src));
  };

  const handleFillPage1 = async () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || !customer || !formData) return;
    const felder = buildVnPage1Felder(consultant, formData);
    const missed = await fillFields(doc, felder);
    if (missed.length) {
      alert(`Nicht gefunden im Formular: ${missed.join(', ')}\n\nBitte manuell eintragen — restliche Felder wurden ausgefüllt.`);
    }
  };

  const handleFillEsfGrundsaetze = async () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || !formData) return;
    const felder = buildVnPage3EsfFelder(formData);
    const missed = await fillFields(doc, felder);
    if (missed.length) {
      alert(`Nicht gefunden im Formular: ${missed.join(', ')}\n\nRestliche (gespeicherte) Antworten wurden eingetragen.`);
    }
  };

  if (!vnOttUrl && !pruefVorgangId) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
        <p style={{ fontSize: 14, color: '#64748b' }}>
          Kein VN-Link — bitte erneut über den grünen Button öffnen.
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
      {/* Schmale Headerleiste */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#fff' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          {customer ? `Verwendungsnachweis — ${customer.name}` : 'BAFA Verwendungsnachweis'}
          {pruefVorgangId && <span style={{ marginLeft: 8, fontWeight: 500, color: '#64748b' }}>· Prüfansicht ohne BAFA-Link</span>}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={runDiagnostics}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            Diagnose
          </button>
          <button
            onClick={() => window.close()}
            title="Tab schließen"
            style={{ display: 'flex', alignItems: 'center', color: '#64748b', background: 'none', border: 'none', padding: 4, cursor: 'pointer', borderRadius: 6 }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Inhalt: iframe + Panel */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {vnOttUrl ? (
          <iframe
            ref={iframeRef}
            src={vnOttUrl}
            style={{ flex: 1, border: 'none' }}
            title="BAFA Verwendungsnachweis"
            onLoad={handleIframeLoad}
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, background: '#f1f5f9' }}>
            <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', maxWidth: 420, lineHeight: 1.6 }}>
              Hier steht sonst das BAFA-Formular.<br />
              In dieser Ansicht bleibt es leer — dafür braucht es den Einmal-Link der BAFA.
              Die Leiste rechts zeigt die echten Daten des Vorgangs.
            </p>
          </div>
        )}
        <div style={{ width: 400, flexShrink: 0, borderLeft: '1px solid #e2e8f0', overflowY: 'auto', background: '#f8fafc' }}>
          <VnDatenPanel
            data={formData}
            loading={formLoading}
            customerId={customer?.id}
            initialNotes={customer?.notes ?? ''}
            onFillPage1={customer && formData && vnOttUrl && bafaPage === 'seite1' ? handleFillPage1 : undefined}
            onFillEsfGrundsaetze={
              formData?.esfSelections && vnOttUrl && bafaPage === 'esf-grundsaetze' ? handleFillEsfGrundsaetze : undefined
            }
            bafaPage={vnOttUrl ? bafaPage : undefined}
          />
        </div>
      </div>

      {debugText && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 16, width: '90%', maxWidth: 700, maxHeight: '80%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Formular-Diagnose (bitte Text kopieren und schicken)</strong>
              <button onClick={() => setDebugText(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={16} />
              </button>
            </div>
            <textarea
              readOnly
              value={debugText}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              style={{ flex: 1, fontFamily: 'monospace', fontSize: 11, resize: 'none', minHeight: 300 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
